import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '../../components/toast/CustomToast';
import Pagination from '../../components/common/Pagination';
import Button from '../../components/common/Button';
import CsvUploadModal from './CsvUploadModal';
import api from '../../utils/api';

const getToken = () => localStorage.getItem('access_token');
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const PAGE_SIZE = 10;

// ── API helpers ────────────────────────────────────────────────────────────────

const examAPI = {
    list: (params = {}) => api.get('/company/exams', { headers: authHeader(), params }),
    create: (data) => api.post('/company/exams', data, { headers: authHeader() }),
    update: (id, data) => api.put(`/company/exams/${id}`, data, { headers: authHeader() }),
    remove: (id) => api.delete(`/company/exams/${id}`, { headers: authHeader() }),
};

const specialtyAPI = {
    list: (params = {}) => api.get('/company/specialties', { headers: authHeader(), params }),
    create: (data) => api.post('/company/specialties', data, { headers: authHeader() }),
    update: (id, data) => api.put(`/company/specialties/${id}`, data, { headers: authHeader() }),
    remove: (id) => api.delete(`/company/specialties/${id}`, { headers: authHeader() }),
};

// ── Field configs ──────────────────────────────────────────────────────────────

const EXAM_FIELDS = [
    { key: 'code',        label: 'Código',   required: true },
    { key: 'description', label: 'Descrição', required: true },
    { key: 'code_tuss',   label: 'TUSS' },
    { key: 'code_amb',    label: 'AMB' },
    { key: 'code_cbhpm',  label: 'CBHPM' },
    { key: 'code_sus',    label: 'SUS' },
    { key: 'code_loinc',  label: 'LOINC' },
    { key: 'code_generic',label: 'Genérico' },
];

const SPECIALTY_FIELDS = [
    { key: 'code',             label: 'Código',          required: true },
    { key: 'description',      label: 'Descrição',        required: true },
    { key: 'code_dicom',       label: 'Sigla Dicom' },
    { key: 'description_dicom',label: 'Descrição Dicom' },
    { key: 'code_generic',     label: 'Genérico' },
];

const emptyForm = (fields) => Object.fromEntries(fields.map(f => [f.key, '']));

// ── Slide Panel ────────────────────────────────────────────────────────────────

const SlidePanel = ({ title, onClose, children }) => (
    <>
        <div className="fixed inset-0 bg-black/50 z-30" onClick={onClose} />
        <div className="fixed right-0 top-0 h-screen w-full max-w-lg bg-white dark:bg-gray-800 shadow-xl z-40 overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
                <button
                    onClick={onClose}
                    className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                >
                    <span className="material-icons" style={{ fontSize: 20 }}>close</span>
                </button>
            </div>
            <div className="p-6 flex-1">{children}</div>
        </div>
    </>
);

// ── Record Form ────────────────────────────────────────────────────────────────

const RecordForm = ({ fields, formData, setFormData, onSubmit, onCancel, isEdit }) => {
    const [errors, setErrors] = useState({});

    const validate = () => {
        const errs = {};
        fields.forEach(f => {
            if (f.required && !formData[f.key]?.trim()) {
                errs[f.key] = `${f.label} é obrigatório`;
            }
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) onSubmit();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map(f => (
                <div key={f.key}>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        {f.label}{f.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                        type="text"
                        value={formData[f.key] ?? ''}
                        onChange={e => {
                            setFormData(prev => ({ ...prev, [f.key]: e.target.value }));
                            if (errors[f.key]) setErrors(prev => ({ ...prev, [f.key]: '' }));
                        }}
                        className={`w-full px-4 py-2 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors[f.key]
                                ? 'border-red-400 dark:border-red-500'
                                : 'border-gray-300 dark:border-gray-600'
                        }`}
                    />
                    {errors[f.key] && (
                        <p className="text-xs text-red-500 mt-1">{errors[f.key]}</p>
                    )}
                </div>
            ))}

            <div className="flex gap-3 pt-4">
                <Button type="submit" variant="primary" className="flex-1">
                    <span className="material-icons" style={{ fontSize: 18 }}>
                        {isEdit ? 'save' : 'add'}
                    </span>
                    {isEdit ? 'Salvar alterações' : 'Adicionar'}
                </Button>
                <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
                    Cancelar
                </Button>
            </div>
        </form>
    );
};

// ── Delete All Modal ───────────────────────────────────────────────────────────

const CONFIRM_PHRASE = 'EXCLUIR TUDO';

const DeleteAllModal = ({ entityLabel, totalCount, onConfirm, onClose }) => {
    const [typed, setTyped] = useState('');
    const confirmed = typed === CONFIRM_PHRASE;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">
                <div className="flex items-center gap-3">
                    <span className="material-icons text-red-500 text-3xl">warning</span>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Excluir todos os registros</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Esta ação não pode ser desfeita</p>
                    </div>
                </div>

                <p className="text-sm text-gray-700 dark:text-gray-300">
                    Você está prestes a excluir <span className="font-semibold text-red-600 dark:text-red-400">{totalCount} {entityLabel.toLowerCase()}{totalCount !== 1 ? 's' : ''}</span> permanentemente.
                </p>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Digite <span className="font-mono font-bold text-red-600 dark:text-red-400">{CONFIRM_PHRASE}</span> para confirmar
                    </label>
                    <input
                        autoFocus
                        type="text"
                        value={typed}
                        onChange={e => setTyped(e.target.value)}
                        placeholder={CONFIRM_PHRASE}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
                    />
                </div>

                <div className="flex gap-3 pt-1">
                    <button
                        onClick={onConfirm}
                        disabled={!confirmed}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition ${
                            confirmed
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        <span className="material-icons" style={{ fontSize: 16 }}>delete_forever</span>
                        Excluir tudo
                    </button>
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-sm transition"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Record Table ───────────────────────────────────────────────────────────────

const RecordTable = ({ items, fields, onEdit, onDelete, selectedIds, onToggle, onToggleAll }) => {
    const allSelected = items.length > 0 && items.every(item => selectedIds.has(item.id));
    const someSelected = items.some(item => selectedIds.has(item.id));

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <th className="px-3 py-2 w-10">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                onChange={() => onToggleAll(items)}
                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-500 text-blue-600 cursor-pointer accent-blue-600"
                                title={allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                            />
                        </th>
                        {fields.map(f => (
                            <th
                                key={f.key}
                                className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide whitespace-nowrap"
                            >
                                {f.label}
                            </th>
                        ))}
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-20">
                            Ações
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {items.map(item => {
                        const isSelected = selectedIds.has(item.id);
                        return (
                            <tr
                                key={item.id}
                                onClick={() => onToggle(item.id)}
                                className={`cursor-pointer transition ${
                                    isSelected
                                        ? 'bg-blue-50 dark:bg-blue-900/20'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                                }`}
                            >
                                <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => onToggle(item.id)}
                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-500 text-blue-600 cursor-pointer accent-blue-600"
                                    />
                                </td>
                                {fields.map((f, idx) => (
                                    <td
                                        key={f.key}
                                        className={`px-3 py-2 text-gray-700 dark:text-gray-200 max-w-[160px] truncate ${
                                            idx === 0
                                                ? 'font-mono font-semibold text-gray-900 dark:text-white'
                                                : ''
                                        }`}
                                        title={item[f.key] || ''}
                                    >
                                        {item[f.key] || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                    </td>
                                ))}
                                <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                                    <div className="flex items-center justify-center gap-2">
                                        <Button onClick={() => onEdit(item)} variant="primary" size="sm" title="Editar">
                                            <span className="material-icons" style={{ fontSize: 16 }}>edit</span>
                                        </Button>
                                        <Button onClick={() => onDelete(item.id)} variant="danger" size="sm" title="Remover">
                                            <span className="material-icons" style={{ fontSize: 16 }}>delete</span>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// ── Main Page ──────────────────────────────────────────────────────────────────

const CompanySettingsPage = () => {
    const [activeTab, setActiveTab] = useState('exams');
    const [searchTerm, setSearchTerm] = useState('');
    const [exams, setExams] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [examTotal, setExamTotal] = useState(0);
    const [specialtyTotal, setSpecialtyTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [panel, setPanel] = useState(null); // null | { mode: 'add'|'edit', item? }
    const [formData, setFormData] = useState({});
    const [csvModal, setCsvModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteAllModal, setDeleteAllModal] = useState(false);

    const isExams = activeTab === 'exams';
    const fields = isExams ? EXAM_FIELDS : SPECIALTY_FIELDS;
    const data = isExams ? exams : specialties;
    const api_ = isExams ? examAPI : specialtyAPI;
    const entityLabel = isExams ? 'Exame' : 'Especialidade';

    const didMountRef = useRef(false);

    const load = useCallback(async (pageNum, search) => {
        setLoading(true);
        try {
            const params = { page: pageNum, page_size: PAGE_SIZE };
            if (search && search.trim()) params.search = search.trim();
            const res = await api_.list(params);
            const items = res.data.items || [];
            const total = res.data.total ?? items.length;
            if (isExams) { setExams(items); setExamTotal(total); }
            else { setSpecialties(items); setSpecialtyTotal(total); }
        } catch {
            if (isExams) { setExams([]); setExamTotal(0); }
            else { setSpecialties([]); setSpecialtyTotal(0); }
        } finally {
            setLoading(false);
        }
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // On mount: load both tabs for badge counts + initial data
    useEffect(() => {
        const loadBoth = async () => {
            setLoading(true);
            try {
                const [examsRes, specialtiesRes] = await Promise.all([
                    examAPI.list({ page: 1, page_size: PAGE_SIZE }),
                    specialtyAPI.list({ page: 1, page_size: PAGE_SIZE }),
                ]);
                setExams(examsRes.data.items || []);
                setExamTotal(examsRes.data.total ?? examsRes.data.items?.length ?? 0);
                setSpecialties(specialtiesRes.data.items || []);
                setSpecialtyTotal(specialtiesRes.data.total ?? specialtiesRes.data.items?.length ?? 0);
            } catch {
                // silent
            } finally {
                setLoading(false);
                didMountRef.current = true;
            }
        };
        loadBoth();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // On tab switch: reset state and reload
    useEffect(() => {
        if (!didMountRef.current) return;
        setPage(1);
        setSearchTerm('');
        setSelectedIds(new Set());
        load(1, '');
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (p) => {
        setPage(p);
        setSelectedIds(new Set());
        load(p, searchTerm);
    };

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setPage(1);
        setSelectedIds(new Set());
        load(1, value);
    };

    const handleToggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleToggleAll = (items) => {
        const allSelected = items.every(item => selectedIds.has(item.id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allSelected) items.forEach(item => next.delete(item.id));
            else items.forEach(item => next.add(item.id));
            return next;
        });
    };

    const handleDeleteSelected = () => {
        const count = selectedIds.size;
        toast.confirm(
            `Excluir ${count} ${entityLabel.toLowerCase()}${count !== 1 ? 's' : ''} selecionado${count !== 1 ? 's' : ''}?`,
            async () => {
                try {
                    await Promise.all([...selectedIds].map(id => api_.remove(id)));
                    toast.success(`${count} ${entityLabel.toLowerCase()}${count !== 1 ? 's' : ''} removido${count !== 1 ? 's' : ''} com sucesso`);
                    setSelectedIds(new Set());
                    load(page, searchTerm);
                } catch (err) {
                    toast.error(`Erro ao remover: ${err.response?.data?.detail || err.message}`);
                }
            }
        );
    };

    const handleDeleteAll = async () => {
        setDeleteAllModal(false);
        setLoading(true);
        try {
            const PAGE = 500;
            let currentPage = 1;
            let allItems = [];
            while (true) {
                const res = await api_.list({ page: currentPage, page_size: PAGE });
                const items = res.data.items || [];
                allItems = allItems.concat(items);
                if (items.length < PAGE) break;
                currentPage++;
            }
            await Promise.all(allItems.map(item => api_.remove(item.id)));
            toast.success(`Todos os registros de ${entityLabel.toLowerCase()} foram removidos`);
            setSelectedIds(new Set());
            setPage(1);
            load(1, searchTerm);
        } catch (err) {
            toast.error(`Erro ao excluir tudo: ${err.response?.data?.detail || err.message}`);
            setLoading(false);
        }
    };

    const total = isExams ? examTotal : specialtyTotal;
    const totalPages = Math.ceil(total / PAGE_SIZE);
    const pagedData = data;

    const openAdd = () => {
        setFormData(emptyForm(fields));
        setPanel({ mode: 'add' });
    };

    const openEdit = (item) => {
        setFormData(Object.fromEntries(fields.map(f => [f.key, item[f.key] ?? ''])));
        setPanel({ mode: 'edit', item });
    };

    const closePanel = () => setPanel(null);

    const handleSubmit = async () => {
        const payload = Object.fromEntries(
            Object.entries(formData).map(([k, v]) => [k, v?.trim() || null])
        );
        try {
            if (panel.mode === 'add') {
                await api_.create(payload);
                toast.success(`${entityLabel} adicionado com sucesso`);
            } else {
                await api_.update(panel.item.id, payload);
                toast.success(`${entityLabel} atualizado com sucesso`);
            }
            closePanel();
            load();
        } catch (err) {
            toast.error(`Erro ao salvar: ${err.response?.data?.detail || err.message}`);
        }
    };

    const handleDelete = (id) => {
        toast.confirm(
            `Confirmar exclusão do(a) ${entityLabel.toLowerCase()}?`,
            async () => {
                try {
                    await api_.remove(id);
                    toast.success(`${entityLabel} removido com sucesso`);
                    load();
                } catch (err) {
                    toast.error(`Erro ao remover: ${err.response?.data?.detail || err.message}`);
                }
            }
        );
    };

    return (
        <div className="flex flex-col h-full gap-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Cadastros</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Gerenciar cadastros da empresa</p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col flex-1 min-h-0">
                {/* Tabs */}
                <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 px-6 pt-4">
                    {[
                        { id: 'exams', label: 'Exames', count: examTotal },
                        { id: 'specialties', label: 'Especialidades', count: specialtyTotal },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-3 font-medium rounded-t-lg transition flex items-center gap-2 ${
                                activeTab === tab.id
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                            }`}
                        >
                            {tab.label}
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                activeTab === tab.id
                                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                            }`}>
                                {tab.count}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex-1 min-w-[200px] flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
                        <span className="material-icons text-gray-400" style={{ fontSize: 18 }}>search</span>
                        <input
                            type="text"
                            placeholder={`Pesquisar ${isExams ? 'exames' : 'especialidades'} por qualquer campo...`}
                            value={searchTerm}
                            onChange={e => handleSearchChange(e.target.value)}
                            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => handleSearchChange('')}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                            >
                                <span className="material-icons" style={{ fontSize: 18 }}>close</span>
                            </button>
                        )}
                    </div>

                    {selectedIds.size > 0 && (
                        <Button onClick={handleDeleteSelected} variant="danger">
                            <span className="material-icons" style={{ fontSize: 18 }}>delete</span>
                            Excluir selecionados ({selectedIds.size})
                        </Button>
                    )}

                    {total > 0 && (
                        <Button onClick={() => setDeleteAllModal(true)} variant="danger" title="Excluir todos os registros">
                            <span className="material-icons" style={{ fontSize: 18 }}>delete_forever</span>
                            Excluir tudo
                        </Button>
                    )}

                    <Button onClick={() => setCsvModal(true)} variant="warning" title="Importar via CSV">
                        <span className="material-icons" style={{ fontSize: 18 }}>upload</span>
                        Importar CSV
                    </Button>
                    <Button onClick={openAdd} variant="primary">
                        <span className="material-icons" style={{ fontSize: 18 }}>add</span>
                        {isExams ? 'Novo Exame' : 'Nova Especialidade'}
                    </Button>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto min-h-0 p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500">
                            <span className="material-icons animate-spin mr-2">refresh</span>
                            Carregando...
                        </div>
                    ) : pagedData.length === 0 ? (
                        <div className="text-center py-16">
                            <span className="material-icons text-4xl text-gray-300 dark:text-gray-600 block mb-2">
                                {isExams ? 'biotech' : 'medical_services'}
                            </span>
                            <p className="text-gray-500 dark:text-gray-400">
                                {searchTerm
                                    ? 'Nenhum resultado encontrado para a pesquisa'
                                    : `Nenhum(a) ${entityLabel.toLowerCase()} cadastrado(a) ainda`}
                            </p>
                        </div>
                    ) : (
                        <RecordTable
                            items={pagedData}
                            fields={fields}
                            onEdit={openEdit}
                            onDelete={handleDelete}
                            selectedIds={selectedIds}
                            onToggle={handleToggleSelect}
                            onToggleAll={handleToggleAll}
                        />
                    )}
                </div>

                {/* Pagination */}
                {totalPages >= 1 && (
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        totalItems={total}
                        itemsPerPage={PAGE_SIZE}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>

            {/* Slide-in panel */}
            {panel && (
                <SlidePanel
                    title={panel.mode === 'add' ? `Adicionar ${entityLabel}` : `Editar ${entityLabel}`}
                    onClose={closePanel}
                >
                    <RecordForm
                        fields={fields}
                        formData={formData}
                        setFormData={setFormData}
                        onSubmit={handleSubmit}
                        onCancel={closePanel}
                        isEdit={panel.mode === 'edit'}
                    />
                </SlidePanel>
            )}

            {/* Delete All confirmation modal */}
            {deleteAllModal && (
                <DeleteAllModal
                    entityLabel={entityLabel}
                    totalCount={total}
                    onConfirm={handleDeleteAll}
                    onClose={() => setDeleteAllModal(false)}
                />
            )}

            {/* CSV Upload modal */}
            {csvModal && (
                <CsvUploadModal
                    fields={fields}
                    entityLabel={entityLabel}
                    entityType={isExams ? 'exam' : 'specialty'}
                    apiPath={isExams ? '/company/exams' : '/company/specialties'}
                    onClose={() => setCsvModal(false)}
                    onSuccess={() => { load(); toast.success('Importação concluída!') }}
                />
            )}
        </div>
    );
};

export default CompanySettingsPage;
